import React, { createContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { apiDelete, get, post, put } from '../../utils/api'

export const PageContext = createContext({
  pages: [],
  getPage: () => Promise.resolve({}),
  addPage: () => Promise.resolve({}),
  savePage: () => Promise.resolve({}),
  archivePage: () => Promise.resolve({}),
  getPages: () => Promise.resolve([]),
  getAllPages: () => Promise.resolve([]),
  login: () => Promise.resolve({}),
  logout: () => Promise.resolve(),
  error: null,
  resetError: () => {},
  me: null,
})

const addPage = page => jwt =>
  post(
    `${process.env.REACT_APP_API_PARTNER_CONTENT_SERVICE_PROXY_PATH}/projects/${process.env.REACT_APP_PARTNER_CONTENT_PROJECT}/pages`,
    page,
    jwt,
  )

const pageUrl = pageId =>
  `${process.env.REACT_APP_API_PARTNER_CONTENT_SERVICE_PROXY_PATH}/projects/${process.env.REACT_APP_PARTNER_CONTENT_PROJECT}/pages/${pageId}`

const getPage = pageId => jwt => get(pageUrl(pageId), jwt)

const getAllPages = jwt =>
  get(
    `${process.env.REACT_APP_API_PARTNER_CONTENT_SERVICE_PROXY_PATH}/projects/${process.env.REACT_APP_PARTNER_CONTENT_PROJECT}/pages`,
    jwt,
  )

const savePage = page => jwt => put(pageUrl(page.id), page, jwt)

const archivePage = page => jwt => apiDelete(pageUrl(page.id), jwt)

const omniAuthUrl = path =>
  `${process.env.REACT_APP_API_OMNI_AUTH_SERVICE_PROXY_PATH}${path}`

const authenticate = ({ username, password }) =>
  post(omniAuthUrl('/login/merchant'), { username, password }).then(response =>
    post(omniAuthUrl('/token/global_administration'), null, response.token),
  )

const refresh = token => get(omniAuthUrl('/token/refresh'), token)

const getPages = () =>
  fetch(
    `${process.env.REACT_APP_API_PARTNER_CONTENT_SERVICE_PROXY_PATH}/public/projects/${process.env.REACT_APP_PARTNER_CONTENT_PROJECT}/pages`,
  )
    .then(r => (r.ok ? r.json() : Promise.reject(Error(':('))))
    .then(pagesResponse =>
      pagesResponse
        .map(page => ({ ...page, key: Math.random() }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    )

const parseMe = jwt => {
  if (!jwt) {
    return null
  }
  const tokenData = JSON.parse(atob(jwt.split('.')[1]))
  return {
    name: tokenData.name,
  }
}

const STORAGE_KEY_TOKEN = 'editor_token'
const STORAGE_KEY_REFRESH_TOKEN = 'editor_refresh'

export const PageContextProvider = ({ children }) => {
  const [pages, setPages] = useState([])
  const [jwt, setJwt] = useState(sessionStorage.getItem(STORAGE_KEY_TOKEN))
  const [refreshToken, setRefreshToken] = useState(
    sessionStorage.getItem(STORAGE_KEY_REFRESH_TOKEN),
  )
  const [me, setMe] = useState()
  const [error, setError] = useState()
  const [trigger, setTrigger] = useState(null)

  const apiReq = apiFunc =>
    jwt
      ? apiFunc(jwt).catch(apiError => {
          if (apiError?.status === 401) {
            if (refreshToken) {
              return refresh(refreshToken)
                .then(response => {
                  setJwt(response.token)
                  setRefreshToken(response.refreshToken)
                  return response.token
                })
                .then(newToken => apiFunc(newToken))
                .catch(() => {
                  setJwt(null)
                  setRefreshToken(null)
                })
            }
            setJwt(null)
          } else {
            toast(apiError.message)
          }
          throw apiError
        })
      : Promise.reject(new Error('unauthorized'))

  useEffect(() => {
    if (me) {
      apiReq(getAllPages)
        .then(response => setPages(response))
        .catch(() => console.info('unable to load pages'))
    }
  }, [trigger, me])

  useEffect(() => {
    setMe(parseMe(jwt))
    if (jwt) {
      sessionStorage.setItem(STORAGE_KEY_TOKEN, jwt)
    } else {
      sessionStorage.removeItem(STORAGE_KEY_TOKEN)
    }
  }, [jwt])

  useEffect(() => {
    if (refreshToken) {
      sessionStorage.setItem(STORAGE_KEY_REFRESH_TOKEN, refreshToken)
    } else {
      sessionStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN)
    }
  }, [refreshToken])

  const triggerUpdate = () => setTrigger(new Date().getTime())

  const value = useMemo(
    () => ({
      pages,
      addPage: page => apiReq(addPage(page)).finally(triggerUpdate),
      savePage: page => apiReq(savePage(page)).finally(triggerUpdate),
      archivePage: page => apiReq(archivePage(page)).finally(triggerUpdate),
      getPage: pageId => apiReq(getPage(pageId)),
      getPages,
      getAllPages: () => apiReq(getAllPages).catch(() => {}),
      login: ({ username, password }) =>
        authenticate({ username, password }).then(response => {
          setJwt(response.token)
          setRefreshToken(response.refreshToken)
        }),
      logout: () => setJwt(null),
      me,
      error,
      resetError: () => setError(null),
    }),
    [pages, me, error],
  )

  return <PageContext.Provider value={value}>{children}</PageContext.Provider>
}
